import {
  Box,
  Divider,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import classNames from 'classnames'
import React from 'react'
import {
  Datagrid,
  DateField,
  EditButton,
  List,
  Pagination,
  ReduxState,
  ResourceContextProvider,
  Show,
  ShowProps,
  SimpleShowLayout,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TitleProps,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { TSCanDto } from '../../../core/dto/device/ts-cans/ts-can.dto'
import { TSCanRemoveCredentialsButton } from './ts-can-show/TSCanRemoveCredentialsButton'
import { TSCanSetCredentialsButton } from './ts-can-show/TSCanSetCredentialsButton'
import { TSCanRestartButton } from './ts-can-show/TSCanRestartButton'

const useStyles = makeStyles((theme: any) => ({
  show: {
    width: `calc(100vw - ${theme?.sidebar?.closedWidth}px - 34px)`,
  },
  showSideOpen: {
    maxWidth: `calc(100vw - ${theme?.sidebar?.width}px - 34px)`,
  },
  showXSmall: {
    width: '100vw',
  },
}))

interface TSCanTitleProps extends TitleProps {
  record?: TSCanDto
}

const ShowTitle = (data: TSCanTitleProps) => {
  const { record: terminal } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.ts-cans.titles.show')}: {terminal?.id}.{' '}
      {terminal?.name}
    </span>
  )
}

const GeneralActions = () => {
  const { record: tscan } = useShowContext<TSCanDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <TSCanSetCredentialsButton />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <TSCanRemoveCredentialsButton />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <TSCanRestartButton />
          </Grid>
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.TS_CANS}`}
              record={tscan}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_TS_CANS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const DevicesGrid = ({ ...props }) => {
  const { value } = props

  return (
    <Datagrid size="small" rowClick={(id) => `/${value}/${id}/show`}>
      <TextField source="id" label="ID" />
      <TextField source="name" />
      <DateField source="lastRefresh" showTime />
    </Datagrid>
  )
}

const DevicesTab = ({ ...props }) => {
  const { value } = props
  const { record: tscan, basePath } = useShowContext()
  return (
    <ResourceContextProvider value={value}>
      <List
        title=" "
        actions={false}
        exporter={false}
        basePath={basePath}
        filter={{ tsCanId: tscan?.id }}
        filters={undefined}
        perPage={20}
        pagination={<Pagination rowsPerPageOptions={[10, 20, 50]} />}
        bulkActionButtons={false}
        empty={false}
      >
        <DevicesGrid value={value} />
      </List>
    </ResourceContextProvider>
  )
}

export const TSCanTabs = ({ ...props }) => {
  const { record: tscan } = useShowContext()
  const { simpleView, ...rest } = props
  const hasAuthority = useHasAuthority()

  return (
    <TabbedShowLayout
      {...rest}
      syncWithLocation={!simpleView}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...props}
        />
      }
    >
      <Tab label="resources.ts-cans.tabs.general">
        <SimpleShowLayout {...props}>
          <TextField label="ID" source="id" />
          <TextField source="name" />
          <TextField source="address" />
          <GeneralActions />
        </SimpleShowLayout>
      </Tab>
      {[
        {
          authority: Authority.VIEW_ENGINES,
          resource: ResourceName.ENGINES,
        },
        {
          authority: Authority.VIEW_PASSAGES,
          resource: ResourceName.PASSAGES,
        },
        {
          authority: Authority.VIEW_TERMINALS,
          resource: ResourceName.TERMINALS,
        },
        {
          authority: Authority.VIEW_BARRIERS,
          resource: ResourceName.BARRIERS,
        },
        {
          authority: Authority.VIEW_DOORS,
          resource: ResourceName.DOORS,
        },
        {
          authority: Authority.VIEW_DETECTORS,
          resource: ResourceName.DETECTORS,
        },
      ].map(({ authority, resource }) => {
        const key = `${resource}Ids`
        return (
          tscan?.[key] &&
          tscan[key].length > 0 &&
          hasAuthority(authority) && (
            <Tab path={resource} label={`resources.${resource}.name`} key={key}>
              <DevicesTab value={resource} />
            </Tab>
          )
        )
      })}
    </TabbedShowLayout>
  )
}

export const TSCanShow = (props: ShowProps) => {
  const classes = useStyles()

  const sidebarOpen = useSelector(
    (state: ReduxState) => state.admin.ui.sidebarOpen,
  )

  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  return (
    <Show
      {...props}
      actions={false}
      title={<ShowTitle />}
      className={
        !xSmallScreen
          ? classNames(classes.show, sidebarOpen && classes.showSideOpen)
          : classes.showXSmall
      }
    >
      <TSCanTabs />
    </Show>
  )
}
